import { Button, CircularProgress } from '@civilgrid/ui/components';
import { Link, useParams } from '@tanstack/react-router';
import * as turf from '@turf/turf';
import type { Feature, FeatureCollection } from 'geojson';
import type { EasingOptions, LngLatBoundsLike } from 'mapbox-gl';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapBox, {
  AttributionControl,
  Layer,
  type MapRef,
  NavigationControl,
  Source,
  type ViewState,
} from 'react-map-gl/mapbox';
import { useAsyncRetry } from 'react-use';
import { type AgencyGeometriesResponse, getAgencyGeometries as apiGetAgencyGeometries } from '~/api/civilsearch';
import { PROJECT_AREA_FILL_COLOR, PROJECT_AREA_LINE_COLOR, PROJECT_AREA_SELECTED_LINE_COLOR } from '~/colors';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import getConfig from '~/config';
import { buildPaletteMatchExpression, FEATURE_PALETTE, parseConnectbaseInternalId, withFeatureIndex } from './helpers';

const config = getConfig();

const MAP_SOURCE_ID = 'agency-geometries';
const FIBER_LIT_SOURCE_ID = 'agency-fiber-lit-buildings';
const PROJECT_BOUNDARIES_SOURCE_ID = 'agency-project-boundaries';
// Project-area colors come from the shared source of truth (see ~/colors).
const PROJECT_BOUNDARY_LINE_COLOR = PROJECT_AREA_LINE_COLOR;
const PROJECT_BOUNDARY_FILL_COLOR = PROJECT_AREA_FILL_COLOR;
const SELECTED_HIGHLIGHT_COLOR = PROJECT_AREA_SELECTED_LINE_COLOR;

function tryComputeBounds(
  fc: FeatureCollection,
  maxZoom?: number,
): { bounds: LngLatBoundsLike; fitBoundsOptions: EasingOptions } | null {
  try {
    const [minLng, minLat, maxLng, maxLat] = turf.bbox(fc);
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) {
      console.error('Computed bbox contained non-finite values', { minLng, minLat, maxLng, maxLat });
      return null;
    }
    return {
      bounds: [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      fitBoundsOptions: { padding: 40, ...(maxZoom !== undefined ? { maxZoom } : {}) },
    };
  } catch (e) {
    console.error('Failed to compute bbox for agency geometries', e);
    return null;
  }
}

// Per-feature bbox (in the same [west, south, east, north] format mapbox.fitBounds wants).
// Returns null for any feature whose geometry can't produce a finite bbox so callers can skip it.
function tryComputeFeatureBounds(feature: Feature): [[number, number], [number, number]] | null {
  try {
    const [minLng, minLat, maxLng, maxLat] = turf.bbox(feature);
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  } catch {
    return null;
  }
}

// Recursive vertex count — works across LineString/Polygon/Multi*/GeometryCollection.
function countVertices(geom: Feature['geometry'] | null | undefined): number {
  if (!geom) return 0;
  if (geom.type === 'GeometryCollection') {
    return geom.geometries.reduce((sum, g) => sum + countVertices(g), 0);
  }
  const walk = (v: unknown): number => {
    if (typeof v === 'number') return 0;
    if (Array.isArray(v)) {
      // A coordinate is an array of numbers; anything deeper is a list of coordinates.
      if (v.length > 0 && typeof v[0] === 'number') return 1;
      return v.reduce<number>((sum, child) => sum + walk(child), 0);
    }
    return 0;
  };
  return walk((geom as { coordinates?: unknown }).coordinates);
}

export default function AgencyMap() {
  // strict:false because this component renders under both `agencies/$agencyId`
  // and `agencies/$agencyId/$tab` route ids; guard the undefined case explicitly
  // rather than lying with a non-null cast.
  const params = useParams({ strict: false });
  const agencyId = params?.agencyId;

  useEffect(() => {
    if (!agencyId) return;
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencyMapTabOpened, { agencyId });
  }, [agencyId]);

  const fetchState = useAsyncRetry<AgencyGeometriesResponse>(async () => {
    if (!agencyId) throw new Error('agencyId missing from route params');
    return apiGetAgencyGeometries(agencyId);
  }, [agencyId]);

  const mapRef = useRef<MapRef | null>(null);
  const setMapRef = useCallback((map: MapRef | null) => {
    mapRef.current = map;
  }, []);

  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);

  const featureRows = useMemo(() => {
    const fc = fetchState.value;
    if (!fc) return [];
    return fc.features.map((feature, index) => {
      const props = (feature.properties ?? {}) as { id?: string; internalId?: string; sourceTable?: string };
      const id = props.id ?? `idx-${index}`;
      const internalId = props.internalId ?? '';
      const projectId = parseConnectbaseInternalId(internalId);
      return {
        id,
        index,
        internalId,
        projectId,
        sourceTable: props.sourceTable ?? '',
        geometryType: feature.geometry?.type ?? 'unknown',
        vertexCount: countVertices(feature.geometry),
        bounds: tryComputeFeatureBounds(feature),
        color: FEATURE_PALETTE[index % FEATURE_PALETTE.length],
      };
    });
  }, [fetchState.value]);

  const handleZoomToFeature = useCallback(
    (rowId: string) => {
      const row = featureRows.find((r) => r.id === rowId);
      if (!row || !row.bounds) return;
      setSelectedFeatureId(rowId);
      mapRef.current?.fitBounds(row.bounds, { padding: 60, duration: 800, maxZoom: 17 });
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencyMapFeatureZoomed, {
        agencyId,
        featureId: rowId,
        geometryType: row.geometryType,
      });
    },
    [agencyId, featureRows],
  );

  // Derive the map-render inputs once per fetch result. Selecting a feature row
  // toggles selectedFeatureId, which would otherwise re-create the GeoJSON source
  // data on every click and reset paint state.
  const mapData = useMemo(() => {
    const fc = fetchState.value;
    if (!fc) return null;
    const fiberLitBuildings = fc.fiberLitBuildings;
    const hasFiberLitBuildings = fiberLitBuildings.features.length > 0;
    // CG-517: fiberlit-only agencies have no line geometries but do have building points.
    if (fc.features.length === 0 && !hasFiberLitBuildings) return null;

    const projectBoundaries = fc.projectBoundaries;
    const hasProjectBoundaries = projectBoundaries.features.length > 0;

    const combinedForBounds: FeatureCollection = {
      type: 'FeatureCollection',
      features: [...fc.features, ...projectBoundaries.features, ...fiberLitBuildings.features],
    };

    return {
      featureCollection: fc,
      projectBoundaries,
      hasProjectBoundaries,
      fiberLitBuildings,
      hasFiberLitBuildings,
      // Point-only bounds can be degenerate (single building) — cap the initial zoom.
      initialViewState: tryComputeBounds(combinedForBounds, fc.features.length === 0 ? 17 : undefined),
      indexedCollection: withFeatureIndex(fc),
      paletteMatchExpression: buildPaletteMatchExpression(fc.features.length),
    };
  }, [fetchState.value]);

  if (!agencyId) {
    return (
      <div className="flex h-full items-center justify-center text-secondary" data-testid="agency-map-missing-id">
        Could not resolve the agency from the URL.
      </div>
    );
  }

  if (fetchState.loading) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="agency-map-loading">
        <CircularProgress indeterminate />
      </div>
    );
  }

  if (fetchState.error) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-2" data-testid="agency-map-error">
        <div>Error fetching geometries for this agency.</div>
        <Button variant="secondary" onClick={() => fetchState.retry()} data-testid="agency-map-retry">
          Try again
        </Button>
      </div>
    );
  }

  if (!mapData) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-2 text-secondary"
        data-testid="agency-map-empty"
      >
        {fetchState.value?.discoverySources?.includes('networkIntelligence') && (
          <p className="max-w-md text-center text-secondary" data-testid="fiberlit-no-points-callout">
            <strong>Fiberlit Building provider.</strong> Discovered via Connectbase network intelligence; no building
            points were returned for this agency.
          </p>
        )}
        No geometries available for this agency.
      </div>
    );
  }

  const {
    featureCollection,
    projectBoundaries,
    hasProjectBoundaries,
    fiberLitBuildings,
    hasFiberLitBuildings,
    initialViewState,
    indexedCollection,
    paletteMatchExpression,
  } = mapData;
  const { total, returned } = featureCollection;
  const truncated = returned < total;

  if (!initialViewState) {
    return (
      <div className="flex h-full items-center justify-center text-secondary" data-testid="agency-map-bbox-error">
        Could not compute map bounds for this agency's geometries.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2" data-testid="agency-map-container">
      {truncated && (
        <p className="shrink-0 text-secondary" data-testid="agency-map-truncated">
          Showing {returned.toLocaleString()} of {total.toLocaleString()} geometries (most recent first).
        </p>
      )}
      <p className="shrink-0">
        <strong>Note:</strong>{' '}
        <em>
          For Connectbase agencies, these geometries reflect only the locations queried for past projects — not the
          agency's full service area.
        </em>
      </p>
      {hasFiberLitBuildings && featureCollection.features.length === 0 && (
        <p className="shrink-0 text-secondary" data-testid="fiberlit-provider-callout">
          <span
            className="mr-1.5 inline-block size-2 rounded-full align-middle"
            style={{ backgroundColor: '#facc15' }}
            aria-hidden
          />
          <strong>Fiberlit Building provider.</strong> Discovered via buildings served by fiber near the project area —
          the points show those buildings.
        </p>
      )}
      <div className="relative min-h-100 w-full flex-1" data-testid="agency-map">
        <MapBox
          ref={setMapRef}
          mapboxAccessToken={config.mapboxToken}
          initialViewState={initialViewState as Partial<ViewState> & typeof initialViewState}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          attributionControl={false}
          style={{ position: 'absolute', inset: 0, borderRadius: '7px' }}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <AttributionControl compact />
          {/* Project boundaries are declared first so they render beneath the agency geometry
              layers — the routes/areas the agency holds for those projects sit on top. */}
          {hasProjectBoundaries && (
            <Source id={PROJECT_BOUNDARIES_SOURCE_ID} type="geojson" data={projectBoundaries}>
              <Layer
                id="project-boundaries-fill"
                source={PROJECT_BOUNDARIES_SOURCE_ID}
                type="fill"
                paint={{ 'fill-color': PROJECT_BOUNDARY_FILL_COLOR, 'fill-opacity': 0.08 }}
              />
              <Layer
                id="project-boundaries-line"
                source={PROJECT_BOUNDARIES_SOURCE_ID}
                type="line"
                paint={{
                  'line-color': PROJECT_BOUNDARY_LINE_COLOR,
                  'line-width': 2.5,
                  'line-dasharray': [3, 1],
                }}
              />
            </Source>
          )}
          {/* Guard: buildPaletteMatchExpression(0) produces an invalid match expression — only render when there are line features. */}
          {featureCollection.features.length > 0 && (
            <Source id={MAP_SOURCE_ID} type="geojson" data={indexedCollection}>
              <Layer
                id="agency-geometries-fill"
                source={MAP_SOURCE_ID}
                type="fill"
                paint={{
                  'fill-color': paletteMatchExpression as never,
                  'fill-opacity': 0.25,
                }}
              />
              <Layer
                id="agency-geometries-line"
                source={MAP_SOURCE_ID}
                type="line"
                paint={{
                  'line-color': paletteMatchExpression as never,
                  'line-width': 2,
                }}
              />
              {selectedFeatureId !== null && (
                <Layer
                  id="agency-geometries-selected-line"
                  source={MAP_SOURCE_ID}
                  type="line"
                  filter={['==', ['get', 'id'], selectedFeatureId]}
                  paint={{
                    'line-color': SELECTED_HIGHLIGHT_COLOR,
                    'line-width': 4,
                  }}
                />
              )}
            </Source>
          )}
          {hasFiberLitBuildings && (
            <Source id={FIBER_LIT_SOURCE_ID} type="geojson" data={fiberLitBuildings}>
              {/* Fixed-size yellow points — focused admin view, no zoom gating. */}
              <Layer
                id="fiber-lit-buildings-circle"
                source={FIBER_LIT_SOURCE_ID}
                type="circle"
                paint={{
                  'circle-radius': 5,
                  'circle-color': '#facc15',
                  'circle-stroke-width': 1.5,
                  'circle-stroke-color': '#ffffff',
                }}
              />
            </Source>
          )}
        </MapBox>
      </div>
      <div
        className="border-grey-30 mt-3 max-h-[40%] overflow-auto rounded-lg border"
        data-testid="agency-map-feature-table"
      >
        <table className="w-full text-sm">
          <thead className="bg-grey-10 sticky top-0 z-10">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">#</th>
              <th className="px-3 py-2 font-medium">Internal ID</th>
              <th className="px-3 py-2 font-medium">Geometry</th>
              <th className="px-3 py-2 font-medium">Source Table</th>
              <th className="px-3 py-2 text-right font-medium">Vertices</th>
              <th className="px-3 py-2 font-medium">Color</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {featureRows.map((row) => {
              const isSelected = row.id === selectedFeatureId;
              const canZoom = !!row.bounds;
              return (
                <tr
                  key={row.id}
                  className={`border-grey-20 cursor-pointer border-t hover:bg-grey-10 ${
                    isSelected ? 'bg-blue-10' : ''
                  } ${canZoom ? '' : 'opacity-50'}`}
                  onClick={() => canZoom && handleZoomToFeature(row.id)}
                  data-testid={`agency-map-feature-row-${row.index}`}
                >
                  <td className="px-3 py-2">{row.index + 1}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.projectId ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: row.projectId }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-60 underline hover:text-blue-70"
                        onClick={(e: MouseEvent) => e.stopPropagation()}
                        data-testid={`agency-map-project-link-${row.index}`}
                      >
                        {row.internalId}
                      </Link>
                    ) : (
                      row.internalId || '—'
                    )}
                  </td>
                  <td className="px-3 py-2">{row.geometryType}</td>
                  <td className="px-3 py-2 font-mono text-xs" data-testid={`agency-map-source-table-${row.index}`}>
                    {row.sourceTable || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.vertexCount.toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block h-3 w-3 rounded-sm align-middle"
                      style={{ backgroundColor: row.color }}
                      aria-hidden
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="small"
                      variant={isSelected ? 'primary' : 'secondary'}
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation();
                        if (canZoom) handleZoomToFeature(row.id);
                      }}
                      disabled={!canZoom}
                      data-testid={`agency-map-zoom-button-${row.index}`}
                    >
                      Zoom
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
